import { parseSafeDate } from '@/utils/dateNormalize';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Trash2, Building2, TrendingUp, Calendar, DollarSign, Pencil, Wallet, User, Users as UsersIcon, ArrowUpRight, ArrowDownRight, AlertTriangle, Download, Info } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';
import { format, subDays, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, differenceInDays, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { usePharmacies } from '../context/PharmacyContext';
import ModernDateRangePicker from '../components/ModernDateRangePicker';
import ExpenseDateRangePicker from '../components/ExpenseDateRangePicker';
import SuperAdminPharmacyFilter from '../components/SuperAdminPharmacyFilter';
import ExpensePharmacyFilter from '../components/ExpensePharmacyFilter';
import useGranularPermissions from '../hooks/useGranularPermissions';
import SecuriteSocialeWizard from '../components/SecuriteSocialeWizard';
import { toSafeISO } from '../utils/dateNormalize';
import { FilterableHeader, SortableHeader, useTableFilters } from '../components/TableFilters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

function SecuriteSociale() {
  const { getPharmacyIdsParam, selectedPharmacyIds } = usePharmacies();
  const {
    canCreate,
    canUpdate,
    canDelete,
    canViewHistory,
    canViewTotals,
    canViewAllUsers,
    canFilterPeriod
  } = useGranularPermissions();
  const [payments, setPayments] = useState([]);
  const [prevPayments, setPrevPayments] = useState([]);  // Paiements du mois précédent (pour comparaison)
  const [view, setView] = useState('entrees');  // 'entrees' | 'rejets'
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [currentUser] = useState(() => JSON.parse(sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user')));
  const [activeTab, setActiveTab] = useState('all');
  const [superAdminPharmacyFilter, setSuperAdminPharmacyFilter] = useState([]);
  const [dateRange, setDateRange] = useState({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date())
  });
  
  const [formData, setFormData] = useState({
    date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    amount: '',
    description: '',
    reference: '',
    category: 'CNAS',
  });


  useEffect(() => {
    fetchPayments();
  }, [selectedPharmacyIds, dateRange]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      const pharmacyIds = getPharmacyIdsParam();
      
      const start = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd'T'00:00:00") : "2000-01-01T00:00:00";
      const end = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd'T'23:59:59") : "2100-12-31T23:59:59";
      
      const response = await axios.get(`${API}/revenues`, { 
        headers,
        params: { 
          type: 'chifa', 
          pharmacy_ids: pharmacyIds,
          start_date: start,
          end_date: end
        }
      });
      setPayments(response.data);

      // Charger aussi le mois précédent (même durée) pour la comparaison
      let prevStart = "1999-01-01T00:00:00";
      let prevEnd = "1999-12-31T23:59:59";
      if (dateRange?.from) {
        const prevFrom = subMonths(dateRange.from, 1);
        const prevTo = subMonths(dateRange.to || dateRange.from, 1);
        prevStart = format(prevFrom, "yyyy-MM-dd'T'00:00:00");
        prevEnd = format(prevTo, "yyyy-MM-dd'T'23:59:59");
      }
      try {
        const prevResp = await axios.get(`${API}/revenues`, {
          headers,
          params: {
            type: 'chifa',
            pharmacy_ids: pharmacyIds,
            start_date: prevStart,
            end_date: prevEnd
          }
        });
        setPrevPayments(prevResp.data || []);
      } catch (e) {
        setPrevPayments([]);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des paiements');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      
      const payload = {
        type: 'chifa',
        ...formData,
        amount: parseFloat(formData.amount),
        date: parseSafeDate(formData.date).toISOString(),
      };

      if (editingPayment) {
        // Mode édition
        await axios.put(`${API}/revenues/${editingPayment.id}`, payload, { headers });
        toast.success('Paiement modifié avec succès!');
      } else {
        // Mode ajout
        await axios.post(`${API}/revenues`, payload, { headers });
        toast.success('Paiement enregistré avec succès!');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchPayments();
    } catch (error) {
      toast.error(editingPayment ? "Erreur lors de la modification" : "Erreur lors de l'enregistrement");
    }
  };

  const handleWizardSubmit = async (wizardData) => {
    if (!currentUser?.id) {
      toast.error('Session expirée. Veuillez vous reconnecter.');
      return;
    }
    
    try {
      const headers = { 'X-User-Id': currentUser.id };
      
      const payload = {
        type: 'chifa',
        date: toSafeISO(wizardData.date),
        amount: parseFloat(wizardData.amount),
        description: wizardData.description || '',
        reference: wizardData.reference || '',
        category: wizardData.category,
        bordereau_number: wizardData.bordereau_number || null,
        liquidation_date: toSafeISO(wizardData.liquidation_date),
        validation_date: toSafeISO(wizardData.validation_date),
        virement_date: toSafeISO(wizardData.virement_date),
        virement_amount: (wizardData.virement_amount !== '' && wizardData.virement_amount != null) ? parseFloat(wizardData.virement_amount) : null,
        montant_bordereau: (wizardData.montant_bordereau !== '' && wizardData.montant_bordereau != null) ? parseFloat(wizardData.montant_bordereau) : null,
        montant_associe: (wizardData.montant_associe !== '' && wizardData.montant_associe != null) ? parseFloat(wizardData.montant_associe) : null,
        montant_officiel: (wizardData.montant_officiel !== '' && wizardData.montant_officiel != null) ? parseFloat(wizardData.montant_officiel) : null,
        montant_majoration: (wizardData.montant_majoration !== '' && wizardData.montant_majoration != null) ? parseFloat(wizardData.montant_majoration) : null,
        montant_officine: (wizardData.montant_officine !== '' && wizardData.montant_officine != null) ? parseFloat(wizardData.montant_officine) : null,
        montant_rejete: (wizardData.montant_rejete !== '' && wizardData.montant_rejete != null) ? parseFloat(wizardData.montant_rejete) : null,
        encaissement_date: toSafeISO(wizardData.encaissement_date),
        is_paid: wizardData.is_paid === true || !!wizardData.bank_account_id || (wizardData.virement_amount !== '' && wizardData.virement_amount != null && parseFloat(wizardData.virement_amount) > 0),
        bank_account_id: wizardData.bank_account_id || null,
        bank_amount: (wizardData.bank_amount !== '' && wizardData.bank_amount != null) ? parseFloat(wizardData.bank_amount) : null,
      };

      if (editingPayment) {
        // Mode édition
        await axios.put(`${API}/revenues/${editingPayment.id}`, payload, { headers });
        toast.success('Paiement modifié avec succès!');
      } else {
        // Mode création
        await axios.post(`${API}/revenues`, payload, { headers });
        toast.success('Paiement enregistré avec succès!');
      }
      
      setWizardOpen(false);
      setEditingPayment(null);
      fetchPayments();
      
    } catch (error) {
      console.error('Erreur:', error, 'Détails API:', error.response?.data);
      if (error.response?.status === 403) {
        console.warn("Accès refusé. Droits administrateur requis.");
      } else if (error.response?.data?.detail) {
        toast.error(`Erreur: ${error.response.data.detail}`);
      } else {
        toast.error(editingPayment ? "Erreur lors de la modification" : "Erreur lors de l'enregistrement du paiement");
      }
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setWizardOpen(true); // Ouvrir le wizard au lieu du dialog
  };

  const resetForm = () => {
    setEditingPayment(null);
    setFormData({
      date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      amount: '',
      description: '',
      reference: '',
      category: 'CNAS',
    });
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce paiement?')) return;

    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      await axios.delete(`${API}/revenues/${paymentId}`, { headers });
      toast.success('Paiement supprimé avec succès');
      fetchPayments();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const togglePaidStatus = async (payment) => {
    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      const newStatus = !payment.is_paid;
      await axios.patch(
        `${API}/revenues/${payment.id}/paid-status`,
        { is_paid: newStatus },
        { headers }
      );
      toast.success(newStatus ? 'Marqué comme Payé' : 'Marqué comme Non payé');
      fetchPayments();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const formatNumberInput = (value) => {
    if (!value) return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return new Intl.NumberFormat('fr-DZ', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-DZ', { 
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value) + ' DA';
  };

  // Filter payments by category
  const getFilteredPayments = () => {
    if (activeTab === 'all') return payments;
    return payments.filter(p => p.category === activeTab);
  };

  // Filtrer selon view_all_users
  const userFilteredPayments = canViewAllUsers('securite_sociale')
    ? payments
    : payments.filter(p => p.created_by === currentUser?.id);

  const filteredPayments = canViewAllUsers('securite_sociale')
    ? getFilteredPayments()
    : getFilteredPayments().filter(p => p.created_by === currentUser?.id);

  const ssFilterConfig = [
    { key: 'pharmacy_name', accessor: (r) => r.pharmacy_name || '—' },
    { key: 'category', accessor: (r) => r.category || '—' },
    { key: 'type', accessor: (r) => r.type || '—' },
    { key: 'created_by_name', accessor: (r) => r.created_by_fullname || r.created_by_username || '—' },
    { key: 'is_paid', accessor: (r) => r.is_paid ? 'Payé' : 'Non payé' },
  ];
  const { filters: ssFilters, setFilter: setSsFilter, getUniqueValues: ssUniqueValues, filteredData: ssFilteredData } = useTableFilters(filteredPayments, ssFilterConfig);
  // Sorting state (default: date desc = recent to old)
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  const handleSort = (key) => {
    setSortConfig(current => {
      if (current.key === key) {
        return { key, direction: current.direction === 'desc' ? 'asc' : 'desc' };
      }
      return { key, direction: 'desc' };
    });
  };

  const displayedPayments = useMemo(() => {
    if (!ssFilteredData || ssFilteredData.length === 0) return ssFilteredData;
    const sorted = [...ssFilteredData];
    const { key, direction } = sortConfig;
    const mult = direction === 'asc' ? 1 : -1;

    sorted.sort((a, b) => {
      let valA = 0;
      let valB = 0;

      if (['date', 'liquidation_date', 'validation_date', 'virement_date', 'encaissement_date'].includes(key)) {
        const dateA = a[key];
        const dateB = b[key];
        valA = dateA ? new Date(dateA).getTime() : 0;
        valB = dateB ? new Date(dateB).getTime() : 0;
      } else if (['amount', 'montant_officine', 'virement_amount', 'montant_rejete'].includes(key)) {
        valA = parseFloat(a[key]) || 0;
        valB = parseFloat(b[key]) || 0;
      }

      if (valA < valB) return -1 * mult;
      if (valA > valB) return 1 * mult;
      return 0;
    });

    return sorted;
  }, [ssFilteredData, sortConfig]);

  // Helper pour matcher les catégories (avec rétro-compatibilité HORS CNAS / HORS CASNOS)
  const matchCategory = (paymentCategory, targetCategory) => {
    if (targetCategory === 'all') return true;
    if (!paymentCategory) return false;
    const cat = String(paymentCategory).trim();
    if (targetCategory === 'CNAS') return cat === 'CNAS';
    if (targetCategory === 'NON CNAS') return cat === 'NON CNAS' || cat === 'HORS CNAS';
    if (targetCategory === 'CASNOS') return cat === 'CASNOS';
    if (targetCategory === 'NON CASNOS') return cat === 'NON CASNOS' || cat === 'HORS CASNOS';
    if (targetCategory === 'Caisse militaire') return cat === 'Caisse militaire' || cat.toLowerCase().includes('militaire');
    return cat === targetCategory;
  };

  // Calculate totals by category (sur données filtrées)
  // Total = somme des Montants Payés (virement_amount), 0 si non renseigné
  const getCategoryTotal = (category) => {
    return userFilteredPayments
      .filter(p => matchCategory(p.category || p.chifa_category, category))
      .reduce((sum, payment) => {
        let val = payment.virement_amount;
        if (val == null || val === '') {
          val = payment.is_paid ? payment.amount : 0;
        }
        return sum + (parseFloat(val) || 0);
      }, 0);
  };

  // Total des Montants Rejetés par catégorie
  const getCategoryRejected = (category) => {
    return userFilteredPayments
      .filter(p => matchCategory(p.category || p.chifa_category, category))
      .reduce((sum, payment) => sum + (payment.montant_rejete || 0), 0);
  };

  const totalAmount = getCategoryTotal('all');
  const chifaTotal = getCategoryTotal('CNAS');
  const horsChifaTotal = getCategoryTotal('NON CNAS');
  const casnosTotal = getCategoryTotal('CASNOS');
  const nonCasnosTotal = getCategoryTotal('NON CASNOS');
  const caisseMedicaleTotal = getCategoryTotal('Caisse militaire');

  const totalRejected = getCategoryRejected('all');
  const chifaRejected = getCategoryRejected('CNAS');
  const horsChifaRejected = getCategoryRejected('NON CNAS');
  const casnosRejected = getCategoryRejected('CASNOS');
  const nonCasnosRejected = getCategoryRejected('NON CASNOS');
  const caisseMedicaleRejected = getCategoryRejected('Caisse militaire');

  // Helpers pour comparaison avec mois précédent
  const sumByCategory = (list, category, field = 'virement_amount') => {
    return list
      .filter(p => matchCategory(p.category || p.chifa_category, category))
      .reduce((s, p) => {
        let val = p[field];
        if (field === 'virement_amount' && (val == null || val === '')) {
          val = p.is_paid ? p.amount : 0;
        }
        return s + (parseFloat(val) || 0);
      }, 0);
  };
  const countByCategory = (list, category) => {
    return list.filter(p => matchCategory(p.category || p.chifa_category, category)).length;
  };
  const variation = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / Math.abs(previous)) * 100;
  };
  const lastPaymentDate = (list, category) => {
    const filtered = list.filter(p => matchCategory(p.category || p.chifa_category, category));
    if (filtered.length === 0) return null;
    return filtered
      .map(p => p.virement_date || p.date)
      .filter(Boolean)
      .sort((a, b) => new Date(b) - new Date(a))[0];
  };

  // Configuration centralisée des organismes (libellé / icône / couleur)
  const organismes = [
    { key: 'CNAS', label: 'CNAS', icon: Building2, color: 'indigo', textColor: 'text-indigo-600', bgIcon: 'bg-indigo-50', borderColor: 'border-l-indigo-500', hex: '#6366f1' },
    { key: 'NON CNAS', label: 'HORS CNAS', icon: User, color: 'blue', textColor: 'text-blue-600', bgIcon: 'bg-blue-50', borderColor: 'border-l-blue-500', hex: '#2563eb' },
    { key: 'CASNOS', label: 'CASNOS', icon: UsersIcon, color: 'orange', textColor: 'text-orange-600', bgIcon: 'bg-orange-50', borderColor: 'border-l-orange-500', hex: '#f97316' },
    { key: 'NON CASNOS', label: 'HORS CASNOS', icon: AlertTriangle, color: 'purple', textColor: 'text-purple-600', bgIcon: 'bg-purple-50', borderColor: 'border-l-purple-500', hex: '#a855f7' },
    { key: 'Caisse militaire', label: 'Caisse militaire', icon: Plus, color: 'teal', textColor: 'text-teal-600', bgIcon: 'bg-teal-50', borderColor: 'border-l-teal-500', hex: '#14b8a6' },
  ];

  // Données pour graphes (cumul journalier)
  const buildEvolutionData = () => {
    if (!dateRange?.from || !dateRange?.to) return [];
    let days = eachDayOfInterval({ start: dateRange.from, end: dateRange.to });
    
    // Ne pas afficher de jours dans le futur (pour le mois en cours)
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    days = days.filter(d => d <= today);
    
    const dayMs = 24 * 60 * 60 * 1000;
    const isRejet = view === 'rejets';
    const field = isRejet ? 'montant_rejete' : 'virement_amount';
    let cumCurr = 0;
    let cumPrev = 0;
    const periodLength = differenceInDays(dateRange.to, dateRange.from);
    return days.map((d) => {
      const dayCurr = userFilteredPayments
        .filter(p => {
          const pd = new Date(p.virement_date || p.date);
          return Math.abs(pd - d) < dayMs && pd.toDateString() === d.toDateString();
        })
        .reduce((s, p) => s + (p[field] || 0), 0);
      // Mois précédent : on regarde le jour - periodLength
      const prevD = new Date(d.getTime() - (periodLength + 1) * dayMs);
      const dayPrev = prevPayments
        .filter(p => {
          const pd = new Date(p.virement_date || p.date);
          return pd.toDateString() === prevD.toDateString();
        })
        .reduce((s, p) => s + (p[field] || 0), 0);
      cumCurr += dayCurr;
      cumPrev += dayPrev;
      return {
        date: format(d, 'd MMM', { locale: fr }),
        current: cumCurr,
        previous: cumPrev,
      };
    });
  };
  const evolutionData = buildEvolutionData();

  // Données donut (répartition)
  const buildDonutData = () => {
    const isRejet = view === 'rejets';
    const field = isRejet ? 'montant_rejete' : 'virement_amount';
    return organismes.map(org => ({
      name: org.label,
      value: sumByCategory(userFilteredPayments, org.key, field),
      hex: org.hex,
    })).filter(d => d.value > 0);
  };
  const donutData = buildDonutData();
  const donutTotal = donutData.reduce((s, d) => s + d.value, 0);

  // Taux de rejet global
  const totalEntreesAll = sumByCategory(userFilteredPayments, 'all', 'virement_amount');
  const totalRejetsAll = sumByCategory(userFilteredPayments, 'all', 'montant_rejete');
  const tauxRejetGlobal = totalEntreesAll > 0 ? (totalRejetsAll / totalEntreesAll) * 100 : 0;
  const prevEntreesAll = sumByCategory(prevPayments, 'all', 'virement_amount');
  const prevRejetsAll = sumByCategory(prevPayments, 'all', 'montant_rejete');
  const prevTauxRejet = prevEntreesAll > 0 ? (prevRejetsAll / prevEntreesAll) * 100 : 0;
  const tauxRejetVar = tauxRejetGlobal - prevTauxRejet;

  // Mini-graph taux de rejet (pts journaliers)
  const tauxRejetSparkline = evolutionData.map(d => ({
    date: d.date,
    taux: d.current > 0 ? Math.min((d.current * 0 + (totalRejetsAll / Math.max(totalEntreesAll, 1)) * 100), 100) : 0,
  }));

  // Organisme à surveiller (taux le plus élevé)
  const organismesParTaux = organismes.map(org => {
    const ent = sumByCategory(userFilteredPayments, org.key, 'virement_amount');
    const rej = sumByCategory(userFilteredPayments, org.key, 'montant_rejete');
    return { ...org, taux: ent > 0 ? (rej / ent) * 100 : 0 };
  }).sort((a, b) => b.taux - a.taux);
  const orgASurveiller = organismesParTaux[0];

  return (
    <div className="space-y-4 fade-in" data-testid="securite-sociale-page">
      {/* En-tête */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Gestion Sécurité Sociale</h1>
        <p className="text-sm text-gray-500">
          {dateRange?.from && format(dateRange.from, 'dd MMM yyyy', { locale: fr })} 
          {dateRange?.to && ` - ${format(dateRange.to, 'dd MMM yyyy', { locale: fr })}`}
        </p>
      </div>

      {/* Filtres Pharmacie + Dates + Action - Modèle unifié */}
      <div className="bg-white rounded-3xl shadow-md p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1 min-w-0">
            {currentUser?.role === 'super_admin' && (
              <ExpensePharmacyFilter
                selectedPharmacies={superAdminPharmacyFilter}
                onChange={setSuperAdminPharmacyFilter}
                currentUser={currentUser}
              />
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {canFilterPeriod('securite_sociale') && (
              <ExpenseDateRangePicker
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
              />
            )}
            {canCreate('securite_sociale') && (
              <button
                onClick={() => setWizardOpen(true)}
                data-testid="add-payment-btn"
                className="flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold transition-all border-2 bg-white text-purple-600 border-purple-200 hover:bg-purple-50 hover:border-purple-300 shadow-sm"
              >
                <Plus className="w-5 h-5" />
                Nouveau Paiement
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Toggle Entrées / Rejets */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => setView('entrees')}
          data-testid="view-entrees-btn"
          className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all border-2 ${
            view === 'entrees'
              ? 'bg-green-600 text-white border-green-600 shadow-lg'
              : 'bg-white text-gray-600 border-gray-200 hover:bg-green-50 hover:border-green-200'
          }`}
        >
          <Wallet className="w-5 h-5" />
          Entrées
        </button>
        <button
          onClick={() => setView('rejets')}
          data-testid="view-rejets-btn"
          className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all border-2 ${
            view === 'rejets'
              ? 'bg-red-600 text-white border-red-600 shadow-lg'
              : 'bg-white text-gray-600 border-gray-200 hover:bg-red-50 hover:border-red-200'
          }`}
        >
          <UsersIcon className="w-5 h-5" />
          Rejets
        </button>
      </div>

      {/* Summary Cards (dynamiques selon view) */}
      {canViewTotals('securite_sociale') ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {/* Carte Total */}
          {(() => {
            const isRejet = view === 'rejets';
            const accent = isRejet ? 'red' : 'green';
            const currentTotal = isRejet
              ? sumByCategory(userFilteredPayments, 'all', 'montant_rejete')
              : sumByCategory(userFilteredPayments, 'all', 'virement_amount');
            const prevTotal = isRejet
              ? sumByCategory(prevPayments, 'all', 'montant_rejete')
              : sumByCategory(prevPayments, 'all', 'virement_amount');
            const v = variation(currentTotal, prevTotal);
            const count = countByCategory(userFilteredPayments, 'all');
            const valueColor = isRejet ? 'text-red-600' : 'text-green-600';
            const borderClass = isRejet ? 'border-l-red-500' : 'border-l-green-500';
            const iconBg = isRejet ? 'bg-red-50' : 'bg-green-50';
            const iconColor = isRejet ? 'text-red-500' : 'text-green-600';
            return (
              <Card className={`stat-card border-l-4 ${borderClass}`}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full ${iconBg} flex items-center justify-center`}>
                      <Wallet className={`w-5 h-5 ${iconColor}`} />
                    </div>
                    <CardTitle className="text-sm font-medium text-gray-600">
                      {isRejet ? 'Total rejeté' : 'Total encaissé'}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${valueColor}`}>{formatCurrency(currentTotal)}</div>
                  <div className="flex items-center gap-1 text-xs mt-1">
                    {v >= 0 ? (
                      <ArrowUpRight className={`w-3.5 h-3.5 ${isRejet ? 'text-red-600' : 'text-green-600'}`} />
                    ) : (
                      <ArrowDownRight className={`w-3.5 h-3.5 ${isRejet ? 'text-green-600' : 'text-red-600'}`} />
                    )}
                    <span className={`font-semibold ${
                      v === 0 ? 'text-gray-500'
                        : (v > 0 ? (isRejet ? 'text-red-600' : 'text-green-600')
                                 : (isRejet ? 'text-green-600' : 'text-red-600'))
                    }`}>
                      {v > 0 ? '+' : ''}{v.toFixed(1)}%
                    </span>
                    <span className="text-gray-500">vs mois précédent</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">{count} paiement{count > 1 ? 's' : ''}</p>
                </CardContent>
              </Card>
            );
          })()}

          {/* Cartes par organisme */}
          {organismes.map(org => {
            const isRejet = view === 'rejets';
            const Icon = org.icon;
            const current = isRejet
              ? sumByCategory(userFilteredPayments, org.key, 'montant_rejete')
              : sumByCategory(userFilteredPayments, org.key, 'virement_amount');
            const prev = isRejet
              ? sumByCategory(prevPayments, org.key, 'montant_rejete')
              : sumByCategory(prevPayments, org.key, 'virement_amount');
            const v = variation(current, prev);
            const count = countByCategory(userFilteredPayments, org.key);
            const titleSuffix = isRejet ? ' rejeté' : '';
            return (
              <Card key={org.key} className={`stat-card border-l-4 ${org.borderColor}`}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full ${org.bgIcon} flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${org.textColor}`} />
                    </div>
                    <CardTitle className="text-sm font-medium text-gray-600">{org.label}{titleSuffix}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${isRejet ? 'text-red-600' : org.textColor}`}>{formatCurrency(current)}</div>
                  <div className="flex items-center gap-1 text-xs mt-1">
                    {v >= 0 ? (
                      <ArrowUpRight className={`w-3.5 h-3.5 ${isRejet ? 'text-red-600' : 'text-green-600'}`} />
                    ) : (
                      <ArrowDownRight className={`w-3.5 h-3.5 ${isRejet ? 'text-green-600' : 'text-red-600'}`} />
                    )}
                    <span className={`font-semibold ${
                      v === 0 ? 'text-gray-500'
                        : (v > 0 ? (isRejet ? 'text-red-600' : 'text-green-600')
                                 : (isRejet ? 'text-green-600' : 'text-red-600'))
                    }`}>
                      {v > 0 ? '+' : ''}{v.toFixed(1)}%
                    </span>
                    <span className="text-gray-500">vs mois précédent</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">{count} paiement{count > 1 ? 's' : ''}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
          <Building2 className="w-8 h-8 mx-auto text-yellow-600 mb-2" />
          <p className="text-sm text-yellow-700">Vous n'avez pas la permission de voir les totaux</p>
        </div>
      )}

      {/* Section graphes : Évolution + Donut + Taux/À surveiller */}
      {canViewTotals('securite_sociale') && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Évolution */}
          <Card className="lg:col-span-5">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">{view === 'rejets' ? 'Évolution des rejets (DA)' : 'Évolution des entrées'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 text-xs text-gray-600 mb-2">
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-full" style={{ background: view === 'rejets' ? '#ef4444' : '#16a34a' }} />
                  {view === 'rejets' ? 'Rejets (DA)' : 'Entrées (DA)'}
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-0.5 border-t-2 border-dashed border-gray-400" />
                  Mois précédent
                </span>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={evolutionData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <defs>
                    <linearGradient id="grad-curr" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={view === 'rejets' ? '#ef4444' : '#16a34a'} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={view === 'rejets' ? '#ef4444' : '#16a34a'} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={(v) => v >= 1e6 ? `${(v / 1e6).toFixed(0)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : v} />
                  <Tooltip formatter={(v) => `${v.toLocaleString('fr-FR')} DA`} />
                  <Area type="monotone" dataKey="current" stroke={view === 'rejets' ? '#ef4444' : '#16a34a'} strokeWidth={2} fill="url(#grad-curr)" dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="previous" stroke="#9ca3af" strokeWidth={1.5} strokeDasharray="5 5" dot={{ r: 2, fill: '#9ca3af' }} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Donut */}
          <Card className="lg:col-span-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">{view === 'rejets' ? 'Répartition des rejets par organisme' : 'Répartition par organisme'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <div className="relative" style={{ width: 180, height: 180 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={donutData} dataKey="value" innerRadius={55} outerRadius={85} paddingAngle={2} stroke="none">
                        {donutData.map((d, i) => <Cell key={i} fill={d.hex} />)}
                      </Pie>
                      <Tooltip formatter={(v) => `${v.toLocaleString('fr-FR')} DA`} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-sm font-bold text-gray-900">{formatCurrency(donutTotal).replace(' DA', '')}</span>
                    <span className="text-[10px] text-gray-500 -mt-1">DA</span>
                    <span className="text-[10px] text-gray-400">Total</span>
                  </div>
                </div>
                <div className="flex-1 space-y-2 text-xs">
                  {donutData.map(d => {
                    const pct = donutTotal > 0 ? (d.value / donutTotal) * 100 : 0;
                    return (
                      <div key={d.name} className="flex items-start gap-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-full mt-1" style={{ background: d.hex }} />
                        <div>
                          <div className="font-semibold text-gray-800">{d.name}</div>
                          <div className="text-gray-500">{pct.toFixed(1)}% ({formatCurrency(d.value)})</div>
                        </div>
                      </div>
                    );
                  })}
                  {donutData.length === 0 && <p className="text-gray-400 italic">Aucune donnée</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Colonne droite : Taux + À surveiller */}
          <div className="lg:col-span-3 space-y-4">
            <Card className={view === 'rejets' ? 'bg-red-50/40' : 'bg-green-50/40'}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                  Taux de rejet global <Info className="w-3.5 h-3.5 text-gray-400" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-4xl font-bold ${view === 'rejets' ? 'text-red-600' : 'text-green-600'}`}>{tauxRejetGlobal.toFixed(1)}%</div>
                <div className="flex items-center gap-1 text-xs mt-1">
                  {tauxRejetVar >= 0
                    ? <ArrowUpRight className={`w-3.5 h-3.5 ${view === 'rejets' ? 'text-red-600' : 'text-green-600'}`} />
                    : <ArrowDownRight className={`w-3.5 h-3.5 ${view === 'rejets' ? 'text-green-600' : 'text-red-600'}`} />}
                  <span className={`font-semibold ${
                    tauxRejetVar === 0 ? 'text-gray-500'
                      : (tauxRejetVar > 0 ? (view === 'rejets' ? 'text-red-600' : 'text-red-600')
                                           : (view === 'rejets' ? 'text-green-600' : 'text-green-600'))
                  }`}>
                    {tauxRejetVar > 0 ? '+' : ''}{tauxRejetVar.toFixed(1)}%
                  </span>
                  <span className="text-gray-500">vs mois précédent</span>
                </div>
                <div style={{ height: 60 }} className="mt-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={tauxRejetSparkline}>
                      <Line type="monotone" dataKey="taux" stroke={view === 'rejets' ? '#ef4444' : '#16a34a'} strokeWidth={1.5} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-700">À surveiller</CardTitle>
              </CardHeader>
              <CardContent>
                {orgASurveiller && orgASurveiller.taux > 0 ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-red-500" />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{orgASurveiller.label}</div>
                        <div className="text-[11px] text-gray-500">Taux de rejet élevé</div>
                      </div>
                    </div>
                    <span className="text-red-600 font-bold text-sm">{orgASurveiller.taux.toFixed(1)}%</span>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">Aucun rejet à signaler</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Détail par organisme */}
      {canViewTotals('securite_sociale') && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>{view === 'rejets' ? 'Détail des rejets par organisme' : 'Détail par organisme'}</CardTitle>
              <CardDescription>Comparaison avec le mois précédent</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organisme</TableHead>
                    <TableHead className="whitespace-nowrap">{view === 'rejets' ? 'Rejets (DA)' : 'Entrées (DA)'}</TableHead>
                    <TableHead className="whitespace-nowrap">vs Mois précédent</TableHead>
                    <TableHead>{view === 'rejets' ? 'Paiements rejetés' : 'Paiements'}</TableHead>
                    <TableHead className="whitespace-nowrap">{view === 'rejets' ? 'Taux de rejet' : 'Rejets (DA)'}</TableHead>
                    <TableHead className="whitespace-nowrap">{view === 'rejets' ? 'Montant total (Entrées)' : 'Taux de rejet'}</TableHead>
                    <TableHead className="whitespace-nowrap">{view === 'rejets' ? 'Dernier rejet' : 'Dernier paiement'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {organismes.map(org => {
                    const Icon = org.icon;
                    const entrees = sumByCategory(userFilteredPayments, org.key, 'virement_amount');
                    const rejets = sumByCategory(userFilteredPayments, org.key, 'montant_rejete');
                    const prevEntrees = sumByCategory(prevPayments, org.key, 'virement_amount');
                    const prevRejets = sumByCategory(prevPayments, org.key, 'montant_rejete');
                    const isRejet = view === 'rejets';
                    const main = isRejet ? rejets : entrees;
                    const prevMain = isRejet ? prevRejets : prevEntrees;
                    const v = variation(main, prevMain);
                    const count = isRejet
                      ? userFilteredPayments.filter(p => p.category === org.key && (p.montant_rejete || 0) > 0).length
                      : countByCategory(userFilteredPayments, org.key);
                    const tauxRejet = entrees > 0 ? (rejets / entrees) * 100 : 0;
                    const last = lastPaymentDate(userFilteredPayments, org.key);
                    return (
                      <TableRow key={org.key}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-full ${org.bgIcon} flex items-center justify-center`}>
                              <Icon className={`w-4 h-4 ${org.textColor}`} />
                            </div>
                            <span className="font-medium">{org.label}</span>
                          </div>
                        </TableCell>
                        <TableCell className={`font-semibold whitespace-nowrap ${isRejet ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(main)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 text-sm font-semibold ${
                            v === 0 ? 'text-gray-500'
                              : (v > 0 ? (isRejet ? 'text-red-600' : 'text-green-600')
                                       : (isRejet ? 'text-green-600' : 'text-red-600'))
                          }`}>
                            {v > 0 ? '+' : ''}{v.toFixed(1)}%
                            {v >= 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
                          </span>
                        </TableCell>
                        <TableCell>{count}</TableCell>
                        <TableCell className="whitespace-nowrap font-semibold text-red-600">
                          {isRejet ? `${tauxRejet.toFixed(1)}%` : formatCurrency(rejets)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {isRejet ? formatCurrency(entrees) : <span className={`font-semibold ${tauxRejet > 3 ? 'text-red-600' : 'text-gray-700'}`}>{tauxRejet.toFixed(1)}%</span>}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-gray-700">
                          {last ? format(new Date(last), 'dd/MM/yyyy', { locale: fr }) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Total row */}
                  {(() => {
                    const totalEntrees = sumByCategory(userFilteredPayments, 'all', 'virement_amount');
                    const totalRejets = sumByCategory(userFilteredPayments, 'all', 'montant_rejete');
                    const prevTotalEntrees = sumByCategory(prevPayments, 'all', 'virement_amount');
                    const prevTotalRejets = sumByCategory(prevPayments, 'all', 'montant_rejete');
                    const isRejet = view === 'rejets';
                    const main = isRejet ? totalRejets : totalEntrees;
                    const prevMain = isRejet ? prevTotalRejets : prevTotalEntrees;
                    const v = variation(main, prevMain);
                    const count = isRejet
                      ? userFilteredPayments.filter(p => (p.montant_rejete || 0) > 0).length
                      : userFilteredPayments.length;
                    const tauxRejet = totalEntrees > 0 ? (totalRejets / totalEntrees) * 100 : 0;
                    const last = lastPaymentDate(userFilteredPayments, 'all');
                    const rowBg = isRejet ? 'bg-red-50' : 'bg-green-50';
                    return (
                      <TableRow className={`font-semibold ${rowBg}`}>
                        <TableCell>Total</TableCell>
                        <TableCell className={`whitespace-nowrap ${isRejet ? 'text-red-700' : 'text-green-700'}`}>{formatCurrency(main)}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 ${
                            v === 0 ? 'text-gray-500'
                              : (v > 0 ? (isRejet ? 'text-red-700' : 'text-green-700')
                                       : (isRejet ? 'text-green-700' : 'text-red-700'))
                          }`}>
                            {v > 0 ? '+' : ''}{v.toFixed(1)}%
                            {v >= 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
                          </span>
                        </TableCell>
                        <TableCell>{count}</TableCell>
                        <TableCell className="whitespace-nowrap text-red-700">
                          {isRejet ? `${tauxRejet.toFixed(1)}%` : formatCurrency(totalRejets)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {isRejet ? formatCurrency(totalEntrees) : `${tauxRejet.toFixed(1)}%`}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm">
                          {last ? format(new Date(last), 'dd/MM/yyyy', { locale: fr }) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs for filtering by category */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="all">Tous</TabsTrigger>
          <TabsTrigger value="CNAS">CNAS</TabsTrigger>
          <TabsTrigger value="NON CNAS">HORS CNAS</TabsTrigger>
          <TabsTrigger value="CASNOS">CASNOS</TabsTrigger>
          <TabsTrigger value="NON CASNOS">HORS CASNOS</TabsTrigger>
          <TabsTrigger value="Caisse militaire">C. Militaire</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {/* Payments Table */}
          {canViewHistory('securite_sociale') ? (
          <Card data-testid="payments-table">
            <CardHeader>
              <CardTitle>
                Historique des Paiements 
                {activeTab !== 'all' && ` - ${activeTab}`}
              </CardTitle>
              <CardDescription>
                {filteredPayments.length} paiement(s) - Total: {formatCurrency(getCategoryTotal(activeTab))}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                </div>
              ) : filteredPayments.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun paiement trouvé</p>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg shadow-sm max-h-[600px] overflow-auto relative">
                    <table className="w-full caption-bottom text-sm text-left">
                      <TableHeader className="sticky top-0 bg-gray-50 z-20 shadow-sm">
                      <TableRow>
                        <TableHead>N° Bordereau</TableHead>
                        <FilterableHeader label="Pharmacie" allLabel="TOUS" values={ssUniqueValues['pharmacy_name'] || []} selected={ssFilters.pharmacy_name} onSelect={(v) => setSsFilter('pharmacy_name', v)} />
                        <FilterableHeader label="Utilisateur" allLabel="TOUS" values={ssUniqueValues['created_by_name'] || []} selected={ssFilters.created_by_name} onSelect={(v) => setSsFilter('created_by_name', v)} />
                        <FilterableHeader label="Catégorie" allLabel="TOUTES" values={ssUniqueValues['category'] || []} selected={ssFilters.category} onSelect={(v) => setSsFilter('category', v)} formatValue={(v) => v === 'NON CNAS' ? 'HORS CNAS' : v === 'NON CASNOS' ? 'HORS CASNOS' : v} />
                        <SortableHeader label="Date dépôt" sortKey="date" currentSort={sortConfig} onSort={handleSort} />
                        <SortableHeader label="Date liquidation" sortKey="liquidation_date" currentSort={sortConfig} onSort={handleSort} />
                        <SortableHeader label="Date validation" sortKey="validation_date" currentSort={sortConfig} onSort={handleSort} />
                        <SortableHeader label="Date virement" sortKey="virement_date" currentSort={sortConfig} onSort={handleSort} />
                        <SortableHeader label="Montant bordereau" sortKey="amount" currentSort={sortConfig} onSort={handleSort} />
                        <SortableHeader label="Montant officine" sortKey="montant_officine" currentSort={sortConfig} onSort={handleSort} />
                        <SortableHeader label="Montant Payé" sortKey="virement_amount" currentSort={sortConfig} onSort={handleSort} />
                        <SortableHeader label="Montant rejeté" sortKey="montant_rejete" currentSort={sortConfig} onSort={handleSort} className="text-red-600" />
                        <SortableHeader label="Date encaissement" sortKey="encaissement_date" currentSort={sortConfig} onSort={handleSort} />
                        <FilterableHeader label="Statut" allLabel="TOUS" values={ssUniqueValues['is_paid'] || []} selected={ssFilters.is_paid} onSelect={(v) => setSsFilter('is_paid', v)} className="whitespace-nowrap" />
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {displayedPayments.map((payment) => (
                        <TableRow key={payment.id} data-testid="payment-row">
                          <TableCell className="font-medium text-gray-700">
                            {payment.bordereau_number || '-'}
                          </TableCell>
                          <TableCell>
                            <Badge className="border bg-gray-50 text-gray-700 border-gray-200">
                              {payment.pharmacy_name || 'N/A'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {payment.created_by_username || 'Système'}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={
                                payment.category === 'CNAS' 
                                  ? 'bg-green-50 text-green-700 border-green-200'
                                  : payment.category === 'NON CNAS' || payment.category === 'HORS CNAS'
                                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                                  : payment.category === 'CASNOS'
                                  ? 'bg-orange-50 text-orange-700 border-orange-200'
                                  : payment.category === 'NON CASNOS' || payment.category === 'HORS CASNOS'
                                  ? 'bg-purple-50 text-purple-700 border-purple-200'
                                  : 'bg-teal-50 text-teal-700 border-teal-200'
                              }
                            >
                              {payment.category === 'NON CNAS' ? 'HORS CNAS' : 
                               payment.category === 'NON CASNOS' ? 'HORS CASNOS' : 
                               payment.category}
                            </Badge>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {format(parseSafeDate(payment.date), 'dd/MM/yyyy', { locale: fr })}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm">
                            {payment.liquidation_date ? format(new Date(payment.liquidation_date), 'dd/MM/yyyy', { locale: fr }) : <span className="text-gray-400 italic">—</span>}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm">
                            {payment.validation_date ? format(new Date(payment.validation_date), 'dd/MM/yyyy', { locale: fr }) : <span className="text-gray-400 italic">—</span>}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm">
                            {payment.virement_date ? format(new Date(payment.virement_date), 'dd/MM/yyyy', { locale: fr }) : <span className="text-gray-400 italic">—</span>}
                          </TableCell>
                          <TableCell className="font-bold text-green-600 whitespace-nowrap">
                            {formatCurrency(payment.amount)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap font-semibold text-gray-800">
                            {payment.montant_officine != null ? formatCurrency(payment.montant_officine) : <span className="text-gray-400 italic font-normal">—</span>}
                          </TableCell>
                          <TableCell className="whitespace-nowrap font-semibold text-blue-700">
                            {(payment.virement_amount != null && payment.virement_amount !== '') ? formatCurrency(payment.virement_amount) : (payment.is_paid ? formatCurrency(payment.amount) : <span className="text-gray-400 italic font-normal">—</span>)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap font-semibold text-red-600">
                            {payment.montant_rejete != null ? formatCurrency(payment.montant_rejete) : <span className="text-gray-400 italic font-normal text-gray-400">—</span>}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm">
                            {payment.encaissement_date ? format(new Date(payment.encaissement_date), 'dd/MM/yyyy', { locale: fr }) : <span className="text-gray-400 italic">—</span>}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {canUpdate('securite_sociale') ? (
                              <button
                                type="button"
                                onClick={() => togglePaidStatus(payment)}
                                data-testid="toggle-paid-btn"
                                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                                  payment.is_paid
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                                }`}
                              >
                                {payment.is_paid ? '✓ Payé' : '✕ Non payé'}
                              </button>
                            ) : (
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                  payment.is_paid
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                                }`}
                              >
                                {payment.is_paid ? '✓ Payé' : '✕ Non payé'}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {canUpdate('securite_sociale') && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(payment)}
                                  data-testid="edit-btn"
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              )}
                              {canDelete('securite_sociale') && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(payment.id)}
                                  data-testid="delete-btn"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <Building2 className="w-8 h-8 mx-auto text-yellow-600 mb-2" />
              <p className="text-sm text-yellow-700">Vous n'avez pas la permission de voir l'historique</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog d'édition Sécurité Sociale */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPayment ? 'Modifier' : 'Ajouter'} Paiement Sécurité Sociale</DialogTitle>
            <DialogDescription>
              Enregistrer un paiement CNAS, HORS CNAS, CASNOS, HORS CASNOS ou Caisse militaire
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="date">Date et heure *</Label>
              <Input
                id="date"
                type="datetime-local"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="category">Catégorie *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger id="category" className="mt-1">
                  <SelectValue placeholder="Sélectionner une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CNAS">CNAS</SelectItem>
                  <SelectItem value="HORS CNAS">HORS CNAS</SelectItem>
                  <SelectItem value="CASNOS">CASNOS</SelectItem>
                  <SelectItem value="HORS CASNOS">HORS CASNOS</SelectItem>
                  <SelectItem value="Caisse militaire">Caisse militaire</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="amount">Montant (DA) *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                className="mt-1"
                placeholder="0.00"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1"
                placeholder="Description du paiement..."
              />
            </div>

            <div>
              <Label htmlFor="reference">Référence</Label>
              <Input
                id="reference"
                type="text"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                className="mt-1"
                placeholder="N° de référence..."
              />
            </div>

            <div className="flex gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setDialogOpen(false);
                  resetForm();
                }}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button type="submit" className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                {editingPayment ? 'Modifier' : 'Ajouter'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Wizard Sécurité Sociale */}
      <SecuriteSocialeWizard
        isOpen={wizardOpen}
        onClose={() => {
          setWizardOpen(false);
          setEditingPayment(null);
        }}
        onSubmit={handleWizardSubmit}
        editMode={!!editingPayment}
        initialData={editingPayment}
        currentUser={currentUser}
      />
    </div>
  );
}

export default SecuriteSociale;
